import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MessageSquare, Save, RotateCcw, Loader2 } from "lucide-react";

const SAMPLE = { order_number: "#H9WLZY", customer_name: "Ava Chen", status_label: "Ready for Pickup",
  portal_link: "https://lowfive.app/portal/abc123", brand: "Low Five Film Lab" };
const preview = (t) => (t || "").replace(/\{(\w+)\}/g, (_, k) => (SAMPLE[k] != null ? SAMPLE[k] : `{${k}}`));

export default function Messages() {
  const { user } = useAuth();
  const isOwner = user?.is_owner;
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/templates"); setItems(r.data.templates); setPlaceholders(r.data.placeholders); }
    catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (id, key, val) => setItems((prev) => prev.map((t) => t.id === id ? { ...t, [key]: val } : t));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/templates", { templates: items });
      toast.success("Message templates saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all messages back to the original wording?")) return;
    try { await api.post("/templates/reset"); toast.success("Reset to defaults"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const emails = items.filter((t) => t.channel === "email");
  const smses = items.filter((t) => t.channel === "sms");

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="mono text-xs uppercase tracking-[0.2em] text-brand mb-2">Customer Notifications</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Messages</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">Edit the wording of every status email and text. Emails keep the Low Five header and a "track your order" button automatically.</p>
        </div>
        {isOwner && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset} data-testid="templates-reset-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
            <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="templates-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}<span className="ml-1.5">Save changes</span>
            </Button>
          </div>
        )}
      </div>

      {!isOwner && <p className="mono text-[11px] text-muted-foreground mb-4">Only the lab owner can edit messages.</p>}

      <div className="bg-card border border-border rounded-xl p-4 mb-6">
        <span className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Placeholders (auto-filled per order)</span>
        <div className="flex flex-wrap gap-2 mt-2">
          {placeholders.map((p) => (
            <button key={p} onClick={() => { navigator.clipboard?.writeText(p); toast.success(`Copied ${p}`); }}
              className="mono text-xs px-2 py-1 rounded bg-secondary text-foreground hover:bg-brand hover:text-primary-foreground transition-colors" data-testid={`ph-${p.replace(/[{}]/g, "")}`}>{p}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="py-16 text-center text-muted-foreground mono text-sm animate-pulse">Loading…</div> : (
        <div className="space-y-8">
          <Section title="Emails" icon={Mail}>
            {emails.map((t) => (
              <div key={t.id} className="bg-card border border-border rounded-xl p-5" data-testid={`tpl-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading font-semibold">{t.label}</span>
                  {t.customized && <span className="mono text-[10px] uppercase tracking-wider text-brand">Customized</span>}
                </div>
                <label className="text-xs text-muted-foreground">Subject</label>
                <Input value={t.subject} onChange={(e) => setField(t.id, "subject", e.target.value)} disabled={!isOwner} className="mt-1 mb-3 mono text-sm" data-testid={`tpl-${t.id}-subject`} />
                <label className="text-xs text-muted-foreground">Body</label>
                <Textarea value={t.body} onChange={(e) => setField(t.id, "body", e.target.value)} disabled={!isOwner} rows={5} className="mt-1 text-sm" data-testid={`tpl-${t.id}-body`} />
                <div className="mt-2 rounded-md bg-secondary p-3 text-xs text-muted-foreground whitespace-pre-wrap"><span className="mono text-[10px] uppercase tracking-wider block mb-1 opacity-70">Preview · {preview(t.subject)}</span>{preview(t.body)}</div>
              </div>
            ))}
          </Section>

          <Section title="Text messages (SMS)" icon={MessageSquare}>
            {smses.map((t) => (
              <div key={t.id} className="bg-card border border-border rounded-xl p-5" data-testid={`tpl-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-heading font-semibold">{t.label}</span>
                  {t.customized && <span className="mono text-[10px] uppercase tracking-wider text-brand">Customized</span>}
                </div>
                <Textarea value={t.text} onChange={(e) => setField(t.id, "text", e.target.value)} disabled={!isOwner} rows={3} className="text-sm" data-testid={`tpl-${t.id}-text`} />
                <div className="mt-2 rounded-md bg-secondary p-3 text-xs text-muted-foreground whitespace-pre-wrap">{preview(t.text)}</div>
              </div>
            ))}
          </Section>
        </div>
      )}
    </Layout>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2 mb-3"><Icon className="w-5 h-5 text-brand" /> {title}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
